import React, { useRef, useState } from 'react'

// Pagina renderizzata (PNG lato server) + overlay SVG delle parole in coordinate PUNTI.
// scale = px/punto. Le parole sono cliccabili (per marcare un valore) e si puo' disegnare
// un rettangolo libero. Coordinate salvate SEMPRE in punti (px / scale).
export default function PageCanvas({ pageUrl, words, pageWidth, pageHeight, scale,
                                    marked, maskDone, selectedField, wordsActive, highlight, intent, mode,
                                    columnOverlays, barcodes, barcodesActive,
                                    onColumnClick, onBarcodeClick, onWordClick, onRectDraw }) {
  // parole cliccabili se c'e' un campo selezionato OPPURE se lo richiede la modalita' (es. Piede, senza campo)
  const clickable = wordsActive !== undefined ? wordsActive : !!selectedField
  const selIdx = new Set((highlight || []).map((w) => w.i))   // parole evidenziate (es. selezione piede)
  const svgRef = useRef(null)
  const [drag, setDrag] = useState(null) // in px

  const W = Math.round(pageWidth * scale)
  const H = Math.round(pageHeight * scale)

  function toLocal(e) {
    const r = svgRef.current.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top }
  }
  function onDown(e) {
    if (e.target.dataset && e.target.dataset.word) return // clic su parola: lo gestisce onClick
    if (e.target.dataset && e.target.dataset.column) return // clic su colonna rilevata: lo gestisce onClick
    if (e.target.dataset && e.target.dataset.barcode) return // clic su barcode: lo gestisce onClick
    const p = toLocal(e)
    setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y })
  }
  function onMove(e) {
    if (!drag) return
    const p = toLocal(e)
    setDrag({ ...drag, x1: p.x, y1: p.y })
  }
  function onUp() {
    if (!drag) { return }
    const x0 = Math.min(drag.x0, drag.x1), x1 = Math.max(drag.x0, drag.x1)
    const y0 = Math.min(drag.y0, drag.y1), y1 = Math.max(drag.y0, drag.y1)
    setDrag(null)
    if (x1 - x0 > 3 && y1 - y0 > 3) {
      onRectDraw({ x0: x0 / scale, y0: y0 / scale, x1: x1 / scale, y1: y1 / scale })
    }
  }

  return (
    <div className={`page-wrap mode-${mode || 'value'}`} style={{ width: W, height: H }}>
      {pageUrl && <img className="page-img" src={pageUrl} width={W} height={H} alt="pagina" />}
      <svg ref={svgRef} className="overlay" width={W} height={H}
           onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}>
        {words.map((w) => (
          <rect key={w.i} data-word="1"
                x={w.x0 * scale} y={w.y0 * scale}
                width={(w.x1 - w.x0) * scale} height={(w.y1 - w.y0) * scale}
                className={(selIdx.has(w.i) ? 'word word-sel' : (clickable ? 'word word-active' : 'word'))}
                onClick={(e) => clickable && onWordClick(w, e.shiftKey)}>
            <title>{w.text}</title>
          </rect>
        ))}
        {maskDone
          ? marked.map((m, idx) => (
              <rect key={`c${idx}`}
                    x={m.x0 * scale - 1} y={m.y0 * scale - 1}
                    width={(m.x1 - m.x0) * scale + 2} height={(m.y1 - m.y0) * scale + 2}
                    className="cover" />
            ))
          : marked.map((m, idx) => (
              <rect key={`m${idx}`}
                    x={m.x0 * scale} y={m.y0 * scale}
                    width={(m.x1 - m.x0) * scale} height={(m.y1 - m.y0) * scale}
                    className={`marked ${m.region_kind || 'value'}`}>
                <title>{m.field_name}{m.token_text ? ` = ${m.token_text}` : ''}</title>
              </rect>
            ))}
        {(intent || []).map((m, idx) => (
          <rect key={`i${idx}`}
                x={m.x0 * scale - 3} y={m.y0 * scale - 3}
                width={(m.x1 - m.x0) * scale + 6} height={(m.y1 - m.y0) * scale + 6}
                className={`intent ${m.region_kind || 'value'}`}>
            <title>{m.field_name || m.region_kind || 'area'}</title>
          </rect>
        ))}
        {(barcodes || []).map((b, idx) => {
          const box = b.bbox || b
          const label = [b.symbology, b.text].filter(Boolean).join(' - ')
          return (
            <g key={`bc-${b.i ?? idx}`} data-barcode="1"
               className={`barcode-hit${barcodesActive ? ' active' : ''}`}
               onClick={(e) => { e.stopPropagation(); barcodesActive && onBarcodeClick && onBarcodeClick(b) }}>
              <rect data-barcode="1"
                    x={Number(box.x0) * scale} y={Number(box.y0) * scale}
                    width={(Number(box.x1) - Number(box.x0)) * scale}
                    height={(Number(box.y1) - Number(box.y0)) * scale}>
                <title>{label || 'barcode'}</title>
              </rect>
              <text data-barcode="1"
                    x={Number(box.x0) * scale} y={Math.max(12, Number(box.y0) * scale - 4)}>
                {b.symbology || 'barcode'}
              </text>
            </g>
          )
        })}
        {(columnOverlays || []).map((m) => (
          <g key={`gap-${m.index}`} data-column="1"
             className={`gap-column${m.selected ? ' sel' : ''}`}
             onClick={(e) => { e.stopPropagation(); onColumnClick && onColumnClick(m.index) }}>
            <rect data-column="1"
                  x={m.x0 * scale} y={m.y0 * scale}
                  width={(m.x1 - m.x0) * scale} height={(m.y1 - m.y0) * scale}>
              <title>colonna {m.index}</title>
            </rect>
            <text data-column="1"
                  x={(m.x0 + m.x1) * scale / 2} y={Math.max(12, m.y0 * scale - 5)}
                  textAnchor="middle">{m.index}</text>
          </g>
        ))}
        {drag && (
          <rect className="drag"
                x={Math.min(drag.x0, drag.x1)} y={Math.min(drag.y0, drag.y1)}
                width={Math.abs(drag.x1 - drag.x0)} height={Math.abs(drag.y1 - drag.y0)} />
        )}
      </svg>
    </div>
  )
}
